import clsx from 'clsx';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
  Children,
} from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { _mergeProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import * as styles from './BaseInput.sty.js';
import { BaseInputButton } from './BaseInputButton.js';
import { BaseInputPrefix, BaseInputSuffix } from './BaseInputContent.js';
import { BaseInputContext } from './contexts/BaseInputContext.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import type { FormControlStateProps } from '../../core/types/form-control-state.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useAutofillEvent } from '../hooks/useAutofillEvent.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlRef } from '../shared-types.js';

/**
 * Props used by all input components.
 * @public
 */
export interface BaseInputProps
  extends ComponentPropsWithoutRef<'input'>,
    FormControlStateProps,
    WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * Variant defining the visual representation of the input field (default or minimal).
   * @defaultValue 'default'
   */
  variant?: 'default' | 'minimal';
}

type SlotContent = { buttons: ReactElement[]; rest: ReactNode[] };

function getSlots(children: ReactNode) {
  const validSlots = Children.toArray(children).filter(
    isValidElement<PropsWithChildren>,
  );

  let prefix: SlotContent = { buttons: [], rest: [] };
  let suffix: SlotContent = { buttons: [], rest: [] };

  for (const slot of validSlots) {
    const buttons: SlotContent['buttons'] = [];
    const rest: SlotContent['rest'] = [];

    const slotChildren = Children.toArray(slot.props.children);

    for (const child of slotChildren) {
      if (isSlot(child, BaseInputButton)) {
        buttons.push(child);
      } else {
        rest.push(child);
      }
    }

    if (slot.type === BaseInputPrefix) {
      prefix = { buttons, rest };
    }
    if (slot.type === BaseInputSuffix) {
      suffix = { buttons, rest };
    }
  }

  return { prefix, suffix };
}

const BaseInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  BaseInputProps
>((props, forwardedRef) => {
  const {
    children,
    id: propId,
    disabled: propDisabled,
    required: propRequired,
    'aria-disabled': ariaDisabledProp,
    variant = 'default',
    controlState,
    readOnly,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  } = props;

  const { disabled, required, id, ariaDisabled } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
  });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const [hasFormControlError, hint] = useFormControlState(controlState);
  // _useInputHint will be replaced with new validation concept. APPDEV-8298
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { inputProps, hintProps } = useInputHint({
    hint,
    hasError: hasFormControlError,
  });

  const { state, dispatch } = useFormFieldMessagesContext();

  const hasError = hasFormControlError || state.showError;

  const { isAutoFilled, onAnimationStart } = useAutofillEvent();

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { focusProps, focusClassName } = useFocusRing({
    color: hasError && !(disabled || ariaDisabled) ? 'critical' : 'neutral',
    ignoreModality: true,
    disabled,
  });

  const { onChange, onBlur, onInvalid, ariaProps } =
    useFormFieldValidation(inputRef);
  const eventListener = { onChange, onBlur, onInvalid };

  useImperativeHandle(
    forwardedRef,
    (): FormControlRef<HTMLDivElement> => ({
      element: wrapperRef.current,
      inputRef: inputRef.current,
      focus() {
        inputRef.current?.focus();
      },
      validate() {
        inputRef.current?.checkValidity();
        dispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    }),
    [dispatch],
  );

  const slots = useMemo(() => getSlots(children), [children]);
  const hasInnerPrefix = slots.prefix.rest.length > 0;
  const hasInnerSuffix = slots.suffix.rest.length > 0;

  return (
    <Flex
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={wrapperRef}
      className={consumerClassName}
      style={consumerStyle}
    >
      <InputGroup
        style={{
          gridAutoColumns: `${'auto '.repeat(
            slots.prefix.buttons.length,
          )}1fr${' auto'.repeat(slots.suffix.buttons.length)}`,
        }}
        className={clsx(styles.wrapper, styles.variant[variant], {
          [styles.hasError]: hasError,
          [styles.disabled]: disabledOrAriaDisabled,
        })}
      >
        <BaseInputContext.Provider
          value={{ variant, hasError, disabled, ariaDisabled }}
        >
          {slots.prefix.buttons}
          <div
            className={clsx(
              styles.container,
              {
                [styles.isAutoFilled]: isAutoFilled,
              },
              inputGroupClassName,
              focusClassName,
            )}
            style={{
              paddingLeft: hasInnerPrefix ? 'unset' : undefined,
              paddingRight: hasInnerSuffix ? 'unset' : undefined,
            }}
            onClick={() => inputRef.current?.focus()} // clicking on the icons should focus the input
          >
            {hasInnerPrefix && (
              <BaseInputPrefix>{slots.prefix.rest}</BaseInputPrefix>
            )}
            <input
              id={id}
              data-testid="text-input"
              ref={inputRef}
              required={required}
              disabled={!!disabled}
              aria-disabled={ariaDisabled}
              onAnimationStart={onAnimationStart}
              readOnly={readOnly}
              className={styles.input}
              {..._mergeProps(
                remainingProps,
                focusProps,
                controlState ? inputProps : ariaProps,
                !controlState ? eventListener : {}, // escape hatch to not interfere with the old implementation
              )}
            />
            {hasInnerSuffix && (
              <BaseInputSuffix>{slots.suffix.rest}</BaseInputSuffix>
            )}
          </div>
          {slots.suffix.buttons}
        </BaseInputContext.Provider>
      </InputGroup>
      {controlState && hint && (
        <BaseItem
          variant={hasError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </Flex>
  );
});

/**
 * Base component used internally for our input components.
 * @internal
 */
export const BaseInput = Object.assign(BaseInputRoot, {
  Prefix: BaseInputPrefix,
  Suffix: BaseInputSuffix,
  Button: BaseInputButton,
});

(BaseInput as typeof BaseInput & { displayName: string }).displayName =
  'BaseInput';
